import React, { useMemo } from 'react';
import styles from './../case-table-view.scss';

import { ColumnDataType, FavouriteDataColumn } from 'app/components/favourite-layout/favourite/common/favourite-interfaces';
import { toHex } from 'ui/common/hex-converter';

import { CASE_NO_COLUMN_NAME, MAX_WIDTH_INCREASE_RATE, 
  columnMinimumWidth } from 'app/area/case-report/case-table-view/constants';
import {  useLocation } from 'react-router-dom';
import { ComplexValue, Entity } from 'app/types-shared';
import { Expandable, ShouldTruncateTextAndLines, standardLinesLimit, standardTextLimit } from 'ui/expandable';
import { useContentRender } from 'ui/link-heading/link-heading';
import { LimitNumberOfLines } from 'app/area/case-report/case-table-view/components/limit-number-of-lines';
import { getDomainAttributesForColumn, getFactItemValues as getSummarySectionValues} 
  from 'app/area/case-report/case-table-view/fact-item.util';
import { DomainAttributes } from 'app/infrastructure/ui/types-uidefinition';
import { summarySectionComponentMap } from 'app/area/case-report/case-table-view/map-summary-section-column';
import { DisplayLink } from 'ui';
import { getUrlOpenCase } from 'app/components/helpers';

interface BaseProps {
  componentId: string;
  column?: Partial<FavouriteDataColumn>;
  isMobileView?: boolean;
  locale?: string;
  tableViewRef?: React.RefObject<HTMLDivElement | null>;
}
type TableCellProps = BaseProps & {
  cells: ComplexValue<number>[];
  dateTimeFormatter?: Intl.DateTimeFormatOptions;
  toCaseView?: () => string;
  location?: ReturnType<typeof useLocation>;
  domainAttributesForRow?: Record<number, DomainAttributes[]>;
  renderAslink?: boolean;
  primaryKey?: number | null;
}

type CaseTableSummarySectionProps = BaseProps & {
  row: Entity[];
  caseId: number;
  domainAttributesForRow: Record<number, DomainAttributes[]>;
  column?: Partial<FavouriteDataColumn>;  
  location?: ReturnType<typeof useLocation>;
  searchParams?: URLSearchParams;
}
const getCellClassName = (cell: ComplexValue<number>, column: Partial<FavouriteDataColumn>) => {
  return [
    column.dataType,
    ...((!!(cell.icon || cell) && [styles.iconColumn]) || [])
  ].join(' ');
};

const getCellProperties = (cell: ComplexValue<number>) => {
    const tooltip = cell.longDescription || cell.description || String(cell.value);
    const backgroundColor = cell.backgroundColor && toHex(cell.backgroundColor);
    const icon = !!cell.icon && <img src={cell.icon} alt={String(cell.value)} />;
    const colorBox = !cell.icon && !!cell.backgroundColor &&
      <div className={styles.colorBox} style={{ backgroundColor }} />;
    const cellValue = `${cell.description || cell.value}`;
    return { tooltip, icon, colorBox, cellValue };
};

const getMinWidth = (column: Partial<FavouriteDataColumn>) => {
    if (column.dataType === 'string') {
      return columnMinimumWidth.typeString;
    }
    if (column.dataType === 'date') {
      return columnMinimumWidth.typeDate;
    }
    if (column.codetable && !column.isHierarchical) { // flat single
      return columnMinimumWidth.codeTableFlat;
    }
    if (column.codetable && column.isHierarchical && !column.isMultipleSelect) {
      return columnMinimumWidth.codeTableHierarchical;
    }
    if (column.codetable && column.isHierarchical && column.isMultipleSelect) {
      return columnMinimumWidth.codeTableHierarchicalMultiple;
    }
    return 200;
};

const buildUrl = (toCaseView: (() => string) | undefined, column: Partial<FavouriteDataColumn>, primaryKey: number | null) => {
  const base = toCaseView?.() || '';
  
  if (primaryKey && column.entityTypePath?.length) {
    const [basePath, queryString] = base.split('?');
    const entityPath = `/${column.entityTypePath.at(-1)}/${primaryKey}`;
    return queryString 
      ? `${basePath}${entityPath}?${queryString}`
      : `${basePath}${entityPath}`;
  }
  
  return base;
};

const getCellContent = ({ cells, column, componentId, isMobileView, toCaseView, primaryKey }: 
  TableCellProps, isSingleExpandable: boolean) => {
  return cells.map((cell, index) => {
    if (!cell.value) {
      return null;
    }
    const minBaseWidth = getMinWidth(column);
    const { tooltip, icon, colorBox, cellValue } = getCellProperties(cell);
    const shouldTruncate = !isSingleExpandable ? ShouldTruncateTextAndLines(cellValue) : false;
    const minWidth = shouldTruncate ? `${minBaseWidth + (cellValue.length * MAX_WIDTH_INCREASE_RATE)}px` : minBaseWidth;
    const contentRender = shouldTruncate ? useContentRender(cellValue, standardTextLimit, standardLinesLimit) : undefined;
    const url = buildUrl(toCaseView, column, primaryKey);
    const isLinkColumn = column.columnName === CASE_NO_COLUMN_NAME || !!primaryKey;
    const linkProps = column.columnName === CASE_NO_COLUMN_NAME
      ? { value: cell.value }
      : { customText: cellValue };
    const widthStyle = minWidth && !isMobileView ? minWidth : '';

    const renderLink = () => {
      const link = <DisplayLink {...linkProps} className={styles.text} url={url} />;
      return primaryKey ? (
        <span style={{ minWidth: column.dataType === ColumnDataType.Integer ? 
          columnMinimumWidth.typeNumber : minWidth }} className={styles.text}>
          {link}
        </span>
      ) : link;
    };

    const renderText = () => (
      <span className={styles.text} style={{ minWidth: widthStyle }}>
        {shouldTruncate ? (
          <span>
            <span className={styles.hideInPrint}><Expandable content={contentRender} /></span>
            <span className={styles.showInPrint}>{cellValue}</span>
          </span>
        ) : cellValue}
        {index !== cells.length - 1 && <span>,</span>}
      </span>
    );

    const value = isLinkColumn ? renderLink() : renderText();
    return <div
      title={tooltip}
      className={getCellClassName(cell, column)}
      key={`${componentId}-${index}`}>
      {icon}
      {colorBox}
      {value}
    </div>;
  });
};

export const DisplayResultValue = ({ cells, column, componentId, isMobileView, toCaseView, location,
  domainAttributesForRow, primaryKey }: TableCellProps) => {

  const domainAttributes = useMemo(
    () => getDomainAttributesForColumn(column.domain as number[], domainAttributesForRow),
    [column.domain, domainAttributesForRow]
  ) as Record<string, string>;

  if (!cells?.length) {
    return null;
  }

  const renderAslink = domainAttributes?.renderAsLink ?? false;

  const isCodeTableFlat = column.codetable && !column.isHierarchical;
  const limitNumberOfLines = isCodeTableFlat && column.isMultipleSelect;
  const values = getCellContent({ cells, column, componentId, isMobileView, toCaseView, 
    location,  
    primaryKey: renderAslink ? primaryKey : null }, limitNumberOfLines);
  return <div
    key={column.columnName}
    className={styles.columnWrapper}>
    {limitNumberOfLines ?
      <LimitNumberOfLines
        elem={values}
        width={columnMinimumWidth.codeTableFlatMultiple} /> : values.map((value) => value)}
  </div>;
};

export function DisplayDateValue({ column, cells,
  componentId, locale, dateTimeFormatter, isMobileView, domainAttributesForRow }: TableCellProps) {
  const attributes = useMemo(
    () => getDomainAttributesForColumn(column.domain as number[], domainAttributesForRow),
    [column.domain, domainAttributesForRow]
  ) as Record<string, string | boolean>;
  const {
    dayFormat,
    monthFormat,
    yearFormat,
    useTime = false
  } = attributes;
  const dateIntlOptions = {
    day: dayFormat || dateTimeFormatter.day,
    month: monthFormat || dateTimeFormatter.month,
    year: yearFormat || dateTimeFormatter.year
  } as Intl.DateTimeFormatOptions;
  const timeIntlOptions: Intl.DateTimeFormatOptions = {
    hour: dateTimeFormatter.hour,
    minute: dateTimeFormatter.minute
  };
  if (!cells?.length) {
    return null;
  }
  const values = cells.map((cell) => {
    if (!cell.value) {
      return null;
    }
    const dateValue: Date = new Date(cell.value);
    if (!dateValue.getTime()) {
      return null;
    }
    const dateFormatter = new Intl.DateTimeFormat(locale, dateIntlOptions);
    const timeFormatter = new Intl.DateTimeFormat(locale, timeIntlOptions);

    const formattedDate = dateFormatter.format(dateValue);
    const formattedTime = useTime ? timeFormatter.format(dateValue) : '';


    return <div
      title={formattedDate}
      key={componentId}
      className={styles.iconColumn}>
      {useTime ? (
        <>
          {formattedDate}
          <br />
          {formattedTime}
        </>
      ) : (
        formattedDate
      )}
    </div>;
  });

  return <div
    key={`${componentId}-data-column`}
    className={`${styles.columnWrapper} ${!isMobileView ? styles.dateColumn : ''}`}>
    {values.map((value) => value)}
  </div>;
}

export const CaseTableSummarySection = ({ row, caseId, componentId, domainAttributesForRow, column, tableViewRef, location, searchParams }
   : CaseTableSummarySectionProps) => {
  const summarySectionDomainAttributes = useMemo(
    () => getDomainAttributesForColumn(column.domain as number[], domainAttributesForRow),
    [column.domain]
  ) as Record<string, string>;

  const summarySectionValues = useMemo(
    () => getSummarySectionValues(row, column, caseId),
    [column.summarySectionTag]
  ) as Record<string, string>;

  const { title: _, ...otherAttributes } = summarySectionDomainAttributes;
  const urlOpenCase = getUrlOpenCase(caseId, location?.pathname, searchParams);
  const factItemAttributes = { ...otherAttributes, caseId, ...summarySectionValues, ...(urlOpenCase && { url: urlOpenCase }), 
    portalTarget: tableViewRef?.current };

  const SummarySectionComponent = summarySectionComponentMap[column.summarySectionTag];
  const renderSummarySection = SummarySectionComponent ? <SummarySectionComponent {...factItemAttributes} /> : null;
  if(!summarySectionValues){
    return null;
  }
  return <div key={`${componentId}-data-column`} className={`compact ${styles.factBox}`}>
    <div >
      {renderSummarySection}
    </div>
  </div>;
};
